/**
 * SOLID Review
 * S — Strong. One concern: the account's use cases, built once.
 * O — Good. A new account act is one use case built here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the account's ops of `ClientUseCases`; the sign-out is also what the session and the legal side sign out with.
 * D — Good. Port interfaces and the shared steps, injected; what it builds is held by role (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  ICommandEmail, ICommandSignUp, ICommandVerifyCode, ICommandReferral, ICommandNickname, ICommandEmailCode,
  IPayloadReferral, IPayloadEmail, IPayloadProfile,
} from '../../domain/ports/schemas/AuthMoves';
import type { IPortClientAuthServer } from '../ports/contracts';
import type { TPortOutcome, TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { ServerRoundTrip } from '../shared/ServerRoundTrip';
import { PassThroughUseCase } from '../shared/PassThroughUseCase';
import type { IConfigDepAuth } from './IConfigDepAuth';
import { RequestLoginCodeUseCase } from './RequestLoginCodeUseCase';
import { SignUpUseCase } from './SignUpUseCase';
import { VerifyCodeUseCase } from './VerifyCodeUseCase';
import { LoadProfileUseCase } from './LoadProfileUseCase';
import { ChangeNicknameUseCase } from './ChangeNicknameUseCase';
import { RequestEmailChangeUseCase } from './RequestEmailChangeUseCase';
import { VerifyEmailChangeUseCase } from './VerifyEmailChangeUseCase';
import { SignOutUseCase } from './SignOutUseCase';

export interface IConfigDepAuthContext {
  readonly decisions: IPortClientDecisions;
  readonly auth: IPortClientAuthServer;
  /** The one serial section, shared with the table and the session. */
  readonly steps: ClientSteps;
}

/**
 * THE ACCOUNT (frontend strict-ports plan, FE3, 2026-10-03; use cases since frontend-application-oo P4). LoginScreen,
 * VerifyCodeScreen, ProfileModal and the welcome screen's logout called the auth REST adapter with the JWT they read from the
 * auth store, and wrote the store around each call. Each is a use case of FE1's pattern now: a step plans (the form's checks),
 * the server is called between two steps, a step answers (what the screen wrote). The token is never here.
 */
export class AuthContext {
  /** The email screen in sign-in mode: ask for a code. */
  readonly requestLoginCode: IUseCase<ICommandEmail, TPortOutcome>;
  readonly signUp: IUseCase<ICommandSignUp, TPortOutcome>;
  readonly verifyCode: IUseCase<ICommandVerifyCode, TPortOutcome>;
  /** The referral field's check on blur: a read with no rule and nothing to keep. */
  readonly checkReferral: IUseCase<ICommandReferral, TPortResult<IPayloadReferral>>;
  readonly loadProfile: IUseCase<ICommandNothing, TPortResult<IPayloadProfile | null>>;
  readonly changeNickname: IUseCase<ICommandNickname, TPortOutcome>;
  readonly requestEmailChange: IUseCase<ICommandEmail, TPortResult<IPayloadEmail>>;
  readonly verifyEmailChange: IUseCase<ICommandEmailCode, TPortOutcome>;
  /** Signing out — also the session's (no player behind the token) and the legal side's (a deleted account). */
  readonly signOut: IUseCase<ICommandNothing, TPortOutcome>;

  constructor(deps: IConfigDepAuthContext) {
    const { decisions, auth, steps } = deps;
    const account: IConfigDepAuth = { decisions, auth, step: steps.onSession, trip: new ServerRoundTrip<IPropsClientInput>(steps.onSession) };
    this.requestLoginCode = new RequestLoginCodeUseCase(account);
    this.signUp = new SignUpUseCase(account);
    this.verifyCode = new VerifyCodeUseCase(account);
    this.checkReferral = new PassThroughUseCase<ICommandReferral, TPortResult<IPayloadReferral>>((ctx, command) => auth.validateReferral(ctx, command));
    this.loadProfile = new LoadProfileUseCase(account);
    this.changeNickname = new ChangeNicknameUseCase(account);
    this.requestEmailChange = new RequestEmailChangeUseCase(account);
    this.verifyEmailChange = new VerifyEmailChangeUseCase(account);
    this.signOut = new SignOutUseCase(account);
  }
}
